import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type {
  ImportError,
  ImportPreview,
  ImportProgress,
  ImportResult,
  ImportStatus,
  KindCounts,
  Member,
} from "../api/types.gen.ts";
import { type Key, t } from "../i18n/index.ts";
import { formatBytes, formatInstant, formatNumber } from "../lib/format.ts";
import { ignore } from "../lib/ignore.ts";
import {
  cancelUpload,
  dismissUpload,
  follow,
  following,
  resumes,
  startUpload,
  type Uploading,
  unfollow,
  uploadEta,
  uploaded,
  uploading,
} from "../lib/imports.ts";
import { orgApi, spaceApi, spaceUrl } from "../lib/paths.ts";
import { useSpacePeople } from "../lib/people.ts";
import { useAction, useResource } from "../lib/resource.ts";
import { location } from "../lib/router.ts";
import { loadMe, type PageProps } from "../lib/state.ts";
import { useId } from "../lib/uid.ts";
import { Button, Link, StepActions } from "../ui/Button.tsx";
import { Dropzone } from "../ui/Dropzone.tsx";
import { Checkbox, Field, Form, Input, Radios, Select } from "../ui/form.tsx";
import { CheckIcon, ImportingIcon, IssuesIcon, TrashIcon, ZipIcon } from "../ui/icons.tsx";
import { Badge, Banner, Notice } from "../ui/misc.tsx";
import { Steps } from "../ui/Steps.tsx";
import { buttonClass } from "../ui/styles.ts";
import { SettingsNav } from "./settings/nav.tsx";

const STEPS = ["file", "review", "apply", "done"] as const;
type Step = (typeof STEPS)[number];
type Target = "here" | "new";
type Conflicts = "newer" | "always" | "skip";
type Reviews = "restore" | "stale" | "skip";
const KINDS = ["entities", "rules", "items", "reviews", "requests", "comments"] as const;
/** A whole, in percent. */
const ALL = 100;
const MINUTE = 60;
const HOUR = 3600;
type KindName = (typeof KINDS)[number];

/** An import, and where it goes. */
interface Current {
  target: Target;
  status: ImportStatus;
}

const percent = (done: number, whole: number) => Math.min(ALL, Math.floor((done / Math.max(whole, 1)) * ALL));

/** "about 3 minutes", "less than a minute". */
function about(seconds: number): string {
  if (seconds < MINUTE) {
    return t("import.eta.seconds");
  }
  if (seconds < HOUR) {
    return t("import.eta.minutes", { count: Math.round(seconds / MINUTE) });
  }
  return t("import.eta.hours", { count: Math.round(seconds / HOUR) });
}

/** What applying would do: records added, updated and removed (reviews only when brought). */
function plan(p: ImportPreview, conflicts: Conflicts, remove: boolean, reviews: Reviews) {
  let add = 0;
  let update = 0;
  let del = 0;
  for (const k of KINDS) {
    if (k === "reviews" && reviews === "skip") {
      continue;
    }
    const c: KindCounts = p[k];
    add += c.new;
    update += updates(c, conflicts);
    del += remove ? c.only_here : 0;
  }
  return { add, update, del };
}

/** Records of a kind a conflict choice updates. */
function updates(c: KindCounts, conflicts: Conflicts): number {
  if (conflicts === "skip") {
    return 0;
  }
  return c.newer + (conflicts === "always" ? c.older : 0);
}

const total = (p: ImportPreview, f: (c: KindCounts) => number) => KINDS.reduce((n, k) => n + f(p[k]), 0);

/** A few numbers side by side: "To add 3 · To update 5 · To remove 0". */
function Stats({ items }: { items: readonly [string, number][] }) {
  return (
    <dl class="flex flex-wrap gap-3">
      {items.map(([label, n]) => (
        <div key={label} class="min-w-28 rounded-lg border border-zinc-200 bg-white px-4 py-3">
          <dt class="text-xs text-zinc-500">{label}</dt>
          <dd class="text-xl font-semibold text-zinc-900 tabular-nums">{formatNumber(n)}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A bar filled to `value` percent (none: still working out how much). */
function Bar({ value }: { value: number | null }) {
  return (
    <div class="h-2 overflow-hidden rounded-full bg-zinc-200">
      <div
        class={`h-full bg-blue-600 transition-all ${value === null ? "w-1/3 animate-pulse" : ""}`}
        style={value === null ? undefined : { width: `${value}%` }}
      />
    </div>
  );
}

/** An upload under way: how far, how long still, and that it's safe (or what went wrong). */
function UploadProgress({ u }: { u: Uploading }) {
  const eta = uploadEta(u);
  if (u.phase === "failed") {
    return (
      <Banner icon={<IssuesIcon />} tone="red" action={{ label: t("import.chooseAnother"), onClick: dismissUpload }}>
        <span>{t("import.uploadFailed", { reason: u.error ?? "" })}</span>
      </Banner>
    );
  }
  return (
    <div class="mt-3 flex flex-col gap-1" role="status">
      <Bar value={u.phase === "reading" ? null : percent(u.sent, u.file.size)} />
      <p class="text-sm text-zinc-700">
        {u.phase === "reading"
          ? t("import.reading")
          : u.phase === "waiting"
            ? t("import.uploadWaiting")
            : t("import.uploadingOf", {
                percent: percent(u.sent, u.file.size),
                sent: formatBytes(u.sent),
                size: formatBytes(u.file.size),
              })}
        {eta === null ? null : ` · ${t("import.timeLeft", { time: about(eta) })}`}
      </p>
      <p class="text-xs text-zinc-500">{t("import.uploadSafe")}</p>
    </div>
  );
}

/** Step 1: where to, and the export (uploaded in pieces, with progress, then read into a preview). */
function ChooseStep(p: {
  space: PageProps["space"];
  target: Target;
  onTarget: (t: Target) => void;
  base: string;
  page: string;
  /** Uploads that stopped, for the server, to go on with the same file. */
  stopped: readonly ImportStatus[];
}) {
  const [file, setFile] = useState<File | null>(null);
  const u = uploading.value;
  const mine = u?.base === p.base ? u : null;
  const busy = Boolean(mine && mine.phase !== "failed");
  const goesOn = file ? p.stopped.find((s) => resumes(file, p.base, s.id)) : undefined;
  return (
    <>
      <div class="mb-4 max-w-2xl">
        <Radios
          label={t("import.target")}
          value={p.target}
          onValue={(v) => !busy && p.onTarget(v)}
          options={[
            {
              value: "here",
              label: t("import.target.here", { space: p.space.name }),
              hint: t("import.target.hereHint"),
            },
            { value: "new", label: t("import.target.new"), hint: t("import.target.newHint") },
          ]}
        />
      </div>
      <div class="max-w-2xl">
        {busy && mine ? (
          <p class="flex items-center gap-2 text-sm text-zinc-700">
            <ZipIcon />
            {`${mine.file.name} · ${formatBytes(mine.file.size)}`}
          </p>
        ) : (
          <>
            <Dropzone label={t("import.drop")} hint={t("import.dropHint")} onFiles={(f) => setFile(f[0] ?? null)} />
            {file ? (
              <p class="mt-3 flex items-center gap-2 text-sm text-zinc-700">
                <ZipIcon />
                {`${file.name} · ${formatBytes(file.size)}`}
              </p>
            ) : null}
            {goesOn ? (
              <p class="mt-1 text-sm text-zinc-600">
                {t("import.goesOn", { percent: percent(goesOn.received ?? 0, goesOn.size ?? 0) })}
              </p>
            ) : null}
          </>
        )}
        {mine ? <UploadProgress u={mine} /> : null}
      </div>
      <StepActions>
        {busy ? (
          <Button onClick={cancelUpload}>{t("import.uploadCancel")}</Button>
        ) : (
          <Button
            variant="primary"
            disabled={!file}
            onClick={() => {
              if (file) {
                const zip = file.type ? file : new File([file], file.name, { type: "application/zip" });
                startUpload(zip, p.base, p.page).catch(ignore);
              }
            }}
          >
            {t("common.continue")}
          </Button>
        )}
      </StepActions>
    </>
  );
}

/** A box of the list of imports waiting: its tone, icon, words and buttons. */
const TONES = {
  info: "border-zinc-200 bg-white",
  amber: "border-amber-300 bg-amber-50",
  red: "border-red-300 bg-red-50",
} as const;

function Box(p: {
  tone: keyof typeof TONES;
  icon: ComponentChildren;
  text: string;
  badge: ComponentChildren;
  children: ComponentChildren;
}) {
  return (
    <div class={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm ${TONES[p.tone]}`}>
      <span class="text-zinc-500">{p.icon}</span>
      <p class="min-w-0 flex-1 text-zinc-800">
        {p.text} {p.badge}
      </p>
      <div class="flex gap-2">{p.children}</div>
    </div>
  );
}

/** One import found when coming back, and what can be done with it. */
function WaitingImport({ c, onOpen, onDiscard }: { c: Current; onOpen: () => void; onDiscard: () => void }) {
  const s = c.status;
  const name = s.source?.name ?? s.file_name ?? "";
  const when = s.source ? formatInstant(s.source.exported_at) : "";
  const expires = formatInstant(s.expires_at);
  const badge = c.target === "new" ? <Badge tone="blue">{t("import.waiting.newSpace")}</Badge> : null;
  const discard = (
    <Button size="sm" onClick={onDiscard}>
      {t("import.waiting.discard")}
    </Button>
  );
  switch (s.state) {
    case "uploading":
      return (
        <Box
          tone="amber"
          icon={<ZipIcon />}
          badge={badge}
          text={t("import.waiting.uploading", {
            file: s.file_name ?? "",
            percent: percent(s.received ?? 0, s.size ?? 0),
            expires,
          })}
        >
          {discard}
        </Box>
      );
    case "staged":
      return (
        <Box tone="info" icon={<ZipIcon />} badge={badge} text={t("import.waiting.staged", { name, when, expires })}>
          <Button size="sm" variant="primary" onClick={onOpen}>
            {t("common.continue")}
          </Button>
          {discard}
        </Box>
      );
    case "applying":
      return (
        <Box tone="info" icon={<ImportingIcon />} badge={badge} text={t("import.waiting.applying", { name })}>
          <Button size="sm" variant="primary" onClick={onOpen}>
            {t("import.waiting.follow")}
          </Button>
        </Box>
      );
    case "failed":
      return (
        <Box
          tone="red"
          icon={<IssuesIcon />}
          badge={badge}
          text={t("import.waiting.failed", { name, reason: s.error?.message ?? "" })}
        >
          <Button size="sm" variant="primary" onClick={onOpen}>
            {t("import.waiting.retry")}
          </Button>
          {discard}
        </Box>
      );
    default:
      return (
        <Box
          tone="info"
          icon={<CheckIcon />}
          badge={badge}
          text={t("import.waiting.done", { name, when: formatInstant(s.finished_at ?? s.expires_at) })}
        >
          <Button size="sm" onClick={onOpen}>
            {t("import.waiting.result")}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDiscard}>
            {t("import.waiting.dismiss")}
          </Button>
        </Box>
      );
  }
}

/** Imports found when coming back: waiting to be checked, stopped mid-upload, running, failed or done. */
function Waiting(p: {
  list: readonly Current[];
  onOpen: (c: Current) => void;
  onDiscard: (c: Current) => void;
  /** An upload this page is sending: not "stopped". */
  sending: string | null;
}) {
  const shown = p.list.filter((c) => c.status.id !== p.sending);
  if (shown.length === 0) {
    return null;
  }
  return (
    <div class="mb-6 flex max-w-3xl flex-col gap-2">
      {shown.map((c) => (
        <WaitingImport key={c.status.id} c={c} onOpen={() => p.onOpen(c)} onDiscard={() => p.onDiscard(c)} />
      ))}
    </div>
  );
}

/** The archive against the space, kind by kind. */
function Comparison({ p, here }: { p: ImportPreview; here: boolean }) {
  const cols: [keyof KindCounts, Key][] = here
    ? [
        ["new", "import.col.new"],
        ["newer", "import.col.newer"],
        ["older", "import.col.older"],
        ["same", "import.col.same"],
        ["only_here", "import.col.onlyHere"],
      ]
    : [["new", "import.col.toAdd"]];
  return (
    <div class="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
      <table class="min-w-full divide-y divide-zinc-200 text-sm">
        <thead class="bg-zinc-50">
          <tr>
            <th scope="col" class="px-3 py-2 text-left font-medium text-zinc-600">
              <span class="sr-only">{t("import.col.kind")}</span>
            </th>
            {cols.map(([c, label]) => (
              <th key={c} scope="col" class="px-3 py-2 text-right font-medium text-zinc-600">
                {t(label)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody class="divide-y divide-zinc-100">
          {KINDS.map((k: KindName) => (
            <tr key={k}>
              <th scope="row" class="px-3 py-2 text-left font-medium text-zinc-800">
                {t(`import.kind.${k}`)}
              </th>
              {cols.map(([c]) => (
                <td key={c} class={`px-3 py-2 text-right tabular-nums ${p[k][c] ? "text-zinc-900" : "text-zinc-400"}`}>
                  {formatNumber(p[k][c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** What a conflict choice does to what's in both, in plain words with the numbers. */
function effects(p: ImportPreview, v: Conflicts): string[] {
  const newer = total(p, (c) => c.newer);
  const older = total(p, (c) => c.older);
  const same = total(p, (c) => c.same);
  const out: string[] = [];
  if (v === "skip") {
    out.push(newer + older > 0 ? t("import.effect.keepAll", { count: newer + older }) : t("import.effect.nothingHere"));
  } else {
    if (newer > 0) {
      out.push(t("import.effect.update", { count: newer }));
    }
    if (older > 0) {
      out.push(
        v === "always" ? t("import.effect.goBack", { count: older }) : t("import.effect.keepNewer", { count: older }),
      );
    }
    if (newer + older === 0) {
      out.push(t("import.effect.nothingHere"));
    }
  }
  if (same > 0) {
    out.push(t("import.effect.same", { count: same }));
  }
  return out;
}

/** "When something is both here and in the backup": three choices as cards, each with its meaning and effect. */
function ConflictChoice(p: { preview: ImportPreview; value: Conflicts; onValue: (v: Conflicts) => void }) {
  const name = useId();
  const options: Conflicts[] = ["newer", "always", "skip"];
  return (
    <fieldset class="flex flex-col gap-2">
      <legend class="mb-2 text-sm font-medium text-zinc-900">{t("import.conflicts")}</legend>
      <div class="grid gap-2 sm:grid-cols-3">
        {options.map((v) => (
          <label
            key={v}
            class={`flex cursor-pointer flex-col gap-1 rounded-lg border bg-white p-3 text-sm has-focus-visible:outline-2 has-focus-visible:outline-blue-600 ${p.value === v ? "border-blue-600 ring-1 ring-blue-600" : "border-zinc-200 hover:border-zinc-300"}`}
          >
            <span class="flex items-center gap-2 font-medium text-zinc-900">
              <input type="radio" name={name} class="sr-only" checked={p.value === v} onChange={() => p.onValue(v)} />
              {t(`import.conflict.${v}`)}
            </span>
            <span class="text-zinc-600">{t(`import.conflict.${v}Hint`)}</span>
            <ul class="mt-auto flex flex-col gap-0.5 pt-1 text-xs text-zinc-500">
              {effects(p.preview, v).map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** Who gets what was of each person of the backup not found here (none: as said, shared and to you). */
function PeopleChoice(p: {
  preview: ImportPreview;
  members: readonly Member[] | undefined;
  value: Record<string, string>;
  onValue: (v: Record<string, string>) => void;
}) {
  const { missing } = p.preview.people;
  if (missing.length === 0) {
    return null;
  }
  const options = [
    { value: "", label: t("import.people.nobody") },
    ...(p.members ?? [])
      .filter((m) => !m.invitation)
      .map((m) => ({ value: m.id, label: m.email ? `${m.name} (${m.email})` : m.name })),
  ];
  return (
    <fieldset class="flex max-w-2xl flex-col gap-3">
      <legend class="mb-1 text-sm font-medium text-zinc-900">{t("import.people.title")}</legend>
      <p class="text-sm text-zinc-600">{t("import.people.hint")}</p>
      {missing.map((m) => (
        <Field key={m.id} label={m.email ? t("import.people.who", { name: m.name, email: m.email }) : m.name}>
          <Select
            value={p.value[m.id] ?? ""}
            options={options}
            onValue={(v) => {
              const next = { ...p.value };
              if (v) {
                next[m.id] = v;
              } else {
                delete next[m.id];
              }
              p.onValue(next);
            }}
          />
        </Field>
      ))}
    </fieldset>
  );
}

interface Choices {
  conflicts: Conflicts;
  remove: boolean;
  reviews: Reviews;
  name: string;
  people: Record<string, string>;
}

/** Step 2: what the export holds against the space, the choices, and what they'll do. */
function ReviewStep(p: {
  space: PageProps["space"];
  preview: ImportPreview;
  target: Target;
  /** Why the last try failed. */
  error: ImportError | null;
  onBack: () => void;
  onApply: (c: Choices) => void;
  busy: boolean;
}) {
  const { preview } = p;
  const here = p.target === "here";
  const members = useSpacePeople(p.space);
  const [conflicts, setConflicts] = useState<Conflicts>("newer");
  const [remove, setRemove] = useState(false);
  const [reviews, setReviews] = useState<Reviews>(preview.same_space ? "restore" : "stale");
  const [name, setName] = useState(preview.source.name);
  const [people, setPeople] = useState<Record<string, string>>({});
  const willDo = plan(preview, conflicts, remove, reviews);
  const onlyHere = total(preview, (c) => c.only_here);
  const files = preview.files;
  const unmatched = preview.people.unmatched - Object.keys(people).length;
  return (
    <Form
      class="flex max-w-4xl flex-col gap-6"
      onSubmit={() => p.onApply({ conflicts, remove, reviews, name, people })}
    >
      {p.error ? (
        <Banner icon={<IssuesIcon />} tone="red">
          <span class="font-medium">{t("import.failed", { reason: p.error.message })}</span>
          <span>{t("import.failedHint")}</span>
        </Banner>
      ) : null}
      <div>
        <p class="flex flex-wrap items-center gap-2 text-sm text-zinc-800">
          <ZipIcon />
          {t("import.from", { name: preview.source.name, when: formatInstant(preview.source.exported_at) })}
          {preview.same_space ? <Badge tone="blue">{t("import.sameSpace")}</Badge> : null}
        </p>
        {preview.same_space ? <p class="mt-1 text-sm text-zinc-500">{t("import.sameSpaceHint")}</p> : null}
        <p class="mt-1 text-sm text-zinc-500">
          {preview.source.history_included ? t("import.history.included") : t("import.history.missing")}
        </p>
        <p class="mt-1 text-xs text-zinc-500">{t("import.expires", { when: formatInstant(preview.expires_at) })}</p>
      </div>
      <Comparison p={preview} here={here} />
      <div class="flex flex-col gap-1 text-sm text-zinc-700">
        {files.to_add > 0 ? <p>{t("import.files", { count: files.to_add })}</p> : null}
        <Notice when={files.missing > 0}>{t("import.filesMissing", { count: files.missing })}</Notice>
        <Notice when={unmatched > 0}>{t("import.people", { count: unmatched })}</Notice>
        <Notice when={here && (preview.schema_locked || preview.rules_locked)}>{t("import.locked")}</Notice>
      </div>
      {here ? (
        <>
          <ConflictChoice preview={preview} value={conflicts} onValue={setConflicts} />
          {onlyHere > 0 ? (
            <div class="flex items-start gap-2">
              <TrashIcon class="mt-0.5 text-red-600" />
              <div>
                <Checkbox label={t("import.remove", { count: onlyHere })} checked={remove} onCheck={setRemove} />
                <p class="text-sm text-zinc-500">{t("import.removeHint")}</p>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div class="max-w-md">
          <Field label={t("import.name")}>
            <Input value={name} onValue={setName} required={true} />
          </Field>
        </div>
      )}
      <PeopleChoice preview={preview} members={members} value={people} onValue={setPeople} />
      {preview.reviews.new + preview.reviews.newer + preview.reviews.older + preview.reviews.same > 0 ? (
        <div class="max-w-2xl">
          {/* Shown; screen readers get it from the radios' own legend. */}
          <p class="mb-2 text-sm font-medium text-zinc-900" aria-hidden="true">
            {t("import.reviews")}
          </p>
          <Radios
            label={t("import.reviews")}
            value={reviews}
            onValue={setReviews}
            options={(["restore", "stale", "skip"] as const).map((v) => ({
              value: v,
              label: t(`import.reviews.${v}`),
              hint: t(`import.reviews.${v}Hint`),
            }))}
          />
        </div>
      ) : null}
      <div>
        <Stats
          items={[
            [t("import.toAdd"), willDo.add],
            ...(here
              ? ([
                  [t("import.toUpdate"), willDo.update],
                  [t("import.toRemove"), willDo.del],
                ] as [string, number][])
              : []),
          ]}
        />
        <p class="mt-2 text-sm text-zinc-500">{here ? t("import.applyHint") : t("import.backgroundHint")}</p>
      </div>
      <StepActions onBack={p.onBack}>
        <Button variant={remove && willDo.del > 0 ? "danger" : "primary"} submit={true} busy={p.busy}>
          {here ? t("import.apply") : t("import.applyNew")}
        </Button>
      </StepActions>
    </Form>
  );
}

/** How far a step has got, in words: "12 of 40 files · 3 MB of 9 MB". */
function stageLine(g: ImportProgress): string | null {
  switch (g.stage) {
    case "files":
      return g.files_total > 0
        ? t("import.progress.files", {
            done: formatNumber(g.files_done),
            total: formatNumber(g.files_total),
            bytes: formatBytes(g.bytes_done),
            totalBytes: formatBytes(g.bytes_total),
          })
        : null;
    case "history":
      return g.history_total > 0
        ? t("import.progress.history", { done: formatNumber(g.history_done), total: formatNumber(g.history_total) })
        : null;
    case "records":
      return g.records_total > 0
        ? t("import.progress.records", { done: formatNumber(g.records_done), total: formatNumber(g.records_total) })
        : null;
    default:
      return null;
  }
}

/** The share of a step done (none: no measure yet). */
function stageShare(g: ImportProgress): number | null {
  switch (g.stage) {
    case "files":
      return g.bytes_total > 0 ? percent(g.bytes_done, g.bytes_total) : null;
    case "history":
      return g.history_total > 0 ? percent(g.history_done, g.history_total) : null;
    case "records":
      return g.records_total > 0 ? percent(g.records_done, g.records_total) : null;
    default:
      return null;
  }
}

/** The steps of applying, in order. */
const STAGES = ["reading", "files", "history", "records"] as const;

/** Step 3: the import running on the server: its steps, how far the current one is, and that it's safe to leave. */
function ApplyStep({ status }: { status: ImportStatus }) {
  const g = status.progress;
  const at = g ? STAGES.indexOf(g.stage) : 0;
  const line = g ? stageLine(g) : null;
  return (
    <div class="flex max-w-2xl flex-col gap-4" role="status">
      <p class="text-sm font-medium text-zinc-900">
        {t("import.applying", { name: status.source?.name ?? status.file_name ?? "" })}
      </p>
      <ol class="flex flex-col gap-1 text-sm">
        {STAGES.map((s, i) => (
          <li
            key={s}
            class={`flex items-center gap-2 ${i < at ? "text-zinc-500" : i === at ? "font-medium text-blue-700" : "text-zinc-400"}`}
          >
            {i < at ? <CheckIcon size={16} /> : <span class="inline-block size-4" />}
            {t(`import.stage.${s}`)}
          </li>
        ))}
      </ol>
      <div class="flex flex-col gap-1">
        <Bar value={g ? stageShare(g) : null} />
        <p class="text-sm text-zinc-700">
          {line ?? t("import.progress.working")}
          {g?.eta_seconds ? ` · ${t("import.timeLeft", { time: about(g.eta_seconds) })}` : null}
        </p>
        {g?.current ? (
          <p class="truncate text-xs text-zinc-500">{t("import.progress.current", { name: g.current })}</p>
        ) : null}
      </div>
      <p class="rounded-md bg-zinc-50 px-4 py-3 text-sm text-zinc-700">{t("import.applySafe")}</p>
    </div>
  );
}

/** Step 4: what changed, and where to see (or undo) it. */
function DoneStep(p: { space: PageProps["space"]; result: ImportResult; name: string | null; onAgain: () => void }) {
  const r = p.result;
  const nothing = r.added + r.updated + r.removed === 0;
  const there = { id: r.space };
  const history = r.history ?? 0;
  return (
    <>
      {nothing ? (
        <p class="mb-4 text-sm text-zinc-700">{t("import.nothing")}</p>
      ) : (
        <div class="mb-4">
          <Stats
            items={[
              [t("import.added"), r.added],
              ...(p.name === null
                ? ([
                    [t("import.updated"), r.updated],
                    [t("import.removed"), r.removed],
                  ] as [string, number][])
                : []),
              [t("import.filesAdded"), r.files],
              ...(history > 0 ? ([[t("import.historyAdded"), history]] as [string, number][]) : []),
            ]}
          />
        </div>
      )}
      {r.commit && p.name === null ? <p class="text-sm text-zinc-600">{t("import.undoHint")}</p> : null}
      <StepActions>
        {p.name === null ? (
          r.commit ? (
            <Link href={spaceUrl(p.space, `/history/${r.commit}`)} class={buttonClass("primary")}>
              {t("import.viewHistory")}
            </Link>
          ) : null
        ) : (
          <Link href={spaceUrl(there, "/items")} class={buttonClass("primary")}>
            {t("import.openSpace", { name: p.name })}
          </Link>
        )}
        <Button variant="ghost" onClick={p.onAgain}>
          {t("import.another")}
        </Button>
      </StepActions>
    </>
  );
}

/** Where an import's step is, by its state. */
function stepOf(c: Current | null): Step {
  switch (c?.status.state) {
    case "staged":
    case "failed":
      return "review";
    case "applying":
      return "apply";
    case "done":
      return "done";
    default:
      return "file";
  }
}

/**
 * Importing an export, in four steps: where to (this space, or a new one) and the file (uploaded in pieces, going on
 * while you're elsewhere, or after a dropped connection); what it holds against the space, with the choices (what
 * wins a conflict, removing what it lacks, reviews, who gets what was of people not here); applying it (on the
 * server, safe to leave); then what changed. Coming back, imports waiting (uploaded, stopped, running, done) are
 * listed to go on with. `?new=1` (from the organization's spaces) starts with a new space.
 */
export function ImportSpace({ space }: PageProps) {
  const [target, setTarget] = useState<Target>(() => (location.value.searchParams.get("new") === "1" ? "new" : "here"));
  const [current, setCurrent] = useState<Current | null>(null);
  // The name given to a new space (the archive's, unless changed).
  const [made, setMade] = useState<string | null>(null);
  const base = (to: Target) => (to === "new" ? orgApi(space, "/imports") : spaceApi(space, "/imports"));
  const page = (to: Target) => spaceUrl(space, to === "new" ? "/settings/import?new=1" : "/settings/import");
  const waiting = useResource(`imports:${space.id}`, async (signal) => {
    const of = (to: Target) =>
      api
        .get<ImportStatus[]>(base(to), undefined, signal)
        .then((l) => l.map((status) => ({ target: to, status })))
        .catch(() => [] as Current[]);
    const [here, fresh] = await Promise.all([of("here"), of("new")]);
    return [...here, ...fresh];
  });
  const step = stepOf(current);
  const path = current ? `${base(current.target)}/${current.status.id}` : "";
  // An upload just finished (here or while elsewhere): checked next.
  const up = uploaded.value;
  useEffect(() => {
    for (const to of ["here", "new"] as const) {
      if (up && up.base === base(to)) {
        uploaded.value = null;
        setTarget(to);
        setCurrent({ target: to, status: up.status });
      }
    }
  });
  // Being applied: followed (from anywhere), and taken up when it ends.
  const f = following.value;
  // biome-ignore lint/correctness/useExhaustiveDependencies: as the import it is, or where it is, changes.
  useEffect(() => {
    if (current?.status.state === "applying") {
      follow(path, page(current.target), f?.path === path ? null : current.status);
    }
  }, [current?.status.id, current?.status.state]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: as what's followed changes state.
  useEffect(() => {
    if (!current || f?.path !== path || !f.status || f.status.state === current.status.state) {
      return;
    }
    const s = f.status;
    if (s.state === "failed") {
      // Its preview, for trying again.
      api
        .get<ImportStatus>(path)
        .then((full) => setCurrent({ target: current.target, status: full }))
        .catch(() => setCurrent({ target: current.target, status: s }));
    } else {
      setCurrent({ target: current.target, status: s });
    }
    if (s.state === "done" && current.target === "new") {
      loadMe().catch(ignore); // the new space, in the switcher
    }
  }, [f?.status?.state]);
  // Done and seen here: no need for the chip.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, when it's done.
  useEffect(() => {
    if (step === "done" && f?.path === path) {
      unfollow();
    }
  }, [step]);
  const open = (c: Current) => {
    setTarget(c.target);
    api
      .get<ImportStatus>(`${base(c.target)}/${c.status.id}`)
      .then((s) => setCurrent({ target: c.target, status: s }))
      .catch(() => waiting.reload());
  };
  const discard = (c: Current) => {
    api
      .del(`${base(c.target)}/${c.status.id}`)
      .catch(ignore)
      .finally(() => waiting.reload());
  };
  const [apply, applying] = useAction(async (c: Choices) => {
    if (!current) {
      return;
    }
    const body =
      current.target === "new"
        ? { name: c.name, reviews: c.reviews, people: c.people }
        : { conflicts: c.conflicts, remove: c.remove, reviews: c.reviews, people: c.people };
    setMade(current.target === "new" ? c.name : null);
    const s = await api.post<ImportStatus>(path, body);
    setCurrent({ target: current.target, status: s });
    follow(path, page(current.target), s);
  });
  const reset = () => {
    setCurrent(null);
    waiting.reload();
  };
  const back = () => {
    if (current) {
      api.del(path).catch(ignore);
    }
    reset();
  };
  const preview = current?.status.preview;
  const result = current?.status.result;
  const mine = uploading.value;
  return (
    <>
      <SettingsNav space={space} tab={target === "new" ? "/org" : ""} />
      <p class="mb-6 max-w-2xl text-sm text-zinc-700">{t("import.intro")}</p>
      <Steps
        labels={[t("import.step.file"), t("import.step.review"), t("import.step.apply"), t("import.step.done")]}
        current={STEPS.indexOf(step)}
      />
      {step === "file" ? (
        <>
          <Waiting
            list={waiting.data ?? []}
            onOpen={(c) => (c.status.state === "uploading" ? undefined : open(c))}
            onDiscard={discard}
            sending={mine?.id ?? null}
          />
          <ChooseStep
            space={space}
            target={target}
            onTarget={setTarget}
            base={base(target)}
            page={page(target)}
            stopped={(waiting.data ?? [])
              .filter((c) => c.target === target && c.status.state === "uploading")
              .map((c) => c.status)}
          />
        </>
      ) : null}
      {step === "review" && current && preview ? (
        <ReviewStep
          space={space}
          preview={preview}
          target={current.target}
          error={current.status.state === "failed" ? (current.status.error ?? null) : null}
          onBack={back}
          onApply={apply}
          busy={applying}
        />
      ) : null}
      {step === "review" && current && !preview ? (
        <Banner icon={<IssuesIcon />} tone="red" action={{ label: t("import.another"), onClick: back }}>
          <span>{t("import.unreadable")}</span>
        </Banner>
      ) : null}
      {step === "apply" && current ? (
        <ApplyStep status={f?.path === path && f.status ? f.status : current.status} />
      ) : null}
      {step === "done" && current && result ? (
        <DoneStep
          space={space}
          result={result}
          name={current.target === "new" ? (made ?? current.status.source?.name ?? "") : null}
          onAgain={() => {
            api.del(path).catch(ignore);
            reset();
          }}
        />
      ) : null}
    </>
  );
}
